<template>
  <div class="page">
    <header class="header">
      <button class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </button>
      <div class="header-title">托盘详情</div>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div v-if="loading" class="status">加载中...</div>
      <div v-else-if="error" class="status">{{ error }}</div>

      <template v-else>
        <section class="panel" v-if="detail">
          <div class="panel-title">托盘信息</div>
          <div class="grid">
            <div class="row">
              <div class="label">托盘码</div>
              <div class="value">{{ detail.palletCode || '-' }}</div>
            </div>
            <div class="row">
              <div class="label">是否空闲</div>
              <div class="value">{{ occupyText }}</div>
            </div>
            <div class="row">
              <div class="label">仓库</div>
              <div class="value">{{ detail.storeName || '-' }}</div>
            </div>
            <div class="row">
              <div class="label">库位</div>
              <div class="value">{{ detail.wareName || '-' }}</div>
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="panel-title">物料列表</div>
          <div v-if="materialList.length === 0" class="status">暂无数据</div>

          <div v-else class="material-list">
            <div class="material-card" v-for="m in materialList" :key="m.id || m.materialNo">
              <div class="material-header">
                <div class="material-no">{{ m.materialNo || '-' }}</div>
                <div class="material-qty">{{ formatQty(m.stockNum) }}{{ m.packageUnitName || '' }}</div>
              </div>

              <div class="batch" v-if="Array.isArray(m.stockList) && m.stockList.length">
                <div class="batch-title">批次明细</div>
                <div class="batch-row" v-for="(s, idx) in m.stockList" :key="(s.batchNo || '') + '-' + idx">
                  <div class="batch-left">
                    <label class="batch-check">
                      <input
                        class="batch-check-input"
                        type="checkbox"
                        :checked="isBatchSelected(m, s)"
                        @change="toggleBatch(m, s)"
                      />
                      <span class="batch-no">{{ s.batchNo || '-' }}</span>
                    </label>
                  </div>
                  <div class="batch-right">
                    <div class="batch-stock">{{ formatQty(s.stockNum) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>

      <div v-if="showMoveModal" class="modal">
        <div class="modal-overlay" @click="closeMoveModal"></div>
        <div class="modal-panel">
          <header class="modal-header">
            <button class="modal-back" @click="closeMoveModal" aria-label="返回">×</button>
            <div class="modal-title">开始移位</div>
            <div class="modal-right"></div>
          </header>

          <div class="modal-body">
            <button class="form-row form-row-btn" type="button" @click="openWareSelector">
              <div class="form-label">新库位</div>
              <div class="form-value" :class="{ placeholder: !newWareText }">{{ newWareText || '请选择新库位' }}</div>
              <div class="form-arrow">›</div>
            </button>
            <div class="form-row">
              <div class="form-label">新托盘码</div>
              <div class="dropdown-wrap">
                <input
                  v-model="palletKeyword"
                  class="form-input"
                  placeholder="请输入新托盘码"
                  @focus="openPalletDropdown"
                  @input="handlePalletInput"
                />

                <div v-if="palletDropdownOpen" class="dropdown">
                  <div v-if="palletLoading" class="status">加载中...</div>
                  <div v-else-if="palletError" class="status">{{ palletError }}</div>
                  <div v-else-if="palletKeyword && palletOptions.length === 0" class="status">暂无数据</div>
                  <div v-else-if="palletOptions.length" class="pallet-options">
                    <button
                      v-for="p in palletOptions"
                      :key="p.id"
                      type="button"
                      class="pallet-option"
                      :class="{ active: selectedNewPalletId === String(p.id) }"
                      @click="confirmNewPallet(p)"
                    >
                      <div class="pallet-option-head">
                        <div class="pallet-option-main">{{ p.palletCode || '-' }}</div>
                        <div class="pallet-badge" :class="badgeClassByOccupy(p.isOccupy)">
                          {{ occupyTextBy(p.isOccupy) }}
                        </div>
                      </div>
                      <div class="pallet-option-sub">
                        {{ p.storeName ? `${p.storeName}` : '' }}
                        {{ p.wareAreaName ? ` | ${p.wareAreaName}` : '' }}
                        {{ p.wareName ? ` | ${p.wareName}` : '' }}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-tip" v-if="selectedNewPalletId">已选新托盘ID：{{ selectedNewPalletId }}</div>

            <div class="form-tip">
              在批次明细中勾选要移位的批次（默认整批移位），再选择新托盘与新库位提交。
            </div>
          </div>

          <div class="modal-footer">
            <button class="selector-confirm" type="button" :disabled="submitLoading" @click="submitMove">
              {{ submitLoading ? '提交中...' : '确认移位' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="showWareSelector" class="selector">
        <div class="selector-overlay" @click="closeWareSelector"></div>
        <div class="selector-panel">
          <header class="selector-header">
            <button class="selector-back" @click="closeWareSelector" aria-label="返回">×</button>
            <div class="selector-title">选择库位</div>
            <div class="selector-right"></div>
          </header>

          <div class="selector-warehouse">{{ warehouseName || '-' }}</div>

          <div v-if="wareSelectorLoading" class="selector-loading">加载中...</div>
          <div v-else-if="wareSelectorError" class="selector-error">{{ wareSelectorError }}</div>

          <div v-else class="selector-body">
            <aside class="areas">
              <button
                v-for="a in wareAreas"
                :key="a.id"
                type="button"
                class="area-item"
                :class="{ active: activeAreaId === a.id }"
                @click="selectArea(a)"
              >
                {{ a.wareName || a.wareNo || '-' }}
              </button>
            </aside>

            <section class="locations">
              <div class="locations-title">{{ activeAreaName }}</div>
              <div class="location-grid">
                <button
                  v-for="l in activeLocations"
                  :key="l.id"
                  type="button"
                  class="location-card"
                  :class="{ selected: selectedLocationId === l.id }"
                  @click="selectLocation(l)"
                >
                  <div class="location-name">{{ l.wareName || l.wareNo || '-' }}</div>
                  <div class="location-sub">剩余托盘位：{{ l.remainPallet ?? '-' }}</div>
                </button>
              </div>
            </section>
          </div>

          <div class="selector-footer">
            <button class="selector-confirm" type="button" :disabled="!selectedLocationId" @click="confirmWareSelector">
              确定
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <button class="action-btn" type="button" :disabled="loading || submitLoading || !detail" @click="openMoveModal">
          {{ submitLoading ? '提交中...' : '开始移位' }}
        </button>
      </div>
    </main>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import toast from '@/utils/toast'
import { getPalletCodeList, getPalletDetail, getWareListByParentId } from '@/api/stockTransfer'
import { changePalletLocation } from '@/api/stockCheck'

export default {
  name: 'StockTransferDetail',
  setup() {
    const route = useRoute()
    const router = useRouter()

    const palletCode = computed(() => String(route.query?.palletCode || '').trim())

    const loading = ref(false)
    const error = ref('')
    const detail = ref(null)

    const occupyText = computed(() => {
      const val = detail.value?.isOccupy
      if (val === 0 || val === '0') return '空闲'
      if (val === 1 || val === '1') return '占用'
      return '-'
    })

    const showMoveModal = ref(false)
    const newWareId = ref('')
    const newWareText = ref('')
    const submitLoading = ref(false)

    const palletDropdownOpen = ref(false)
    const palletKeyword = ref('')
    const palletLoading = ref(false)
    const palletError = ref('')
    const palletOptions = ref([])
    const selectedNewPallet = ref(null)
    const selectedNewPalletId = computed(() => String(selectedNewPallet.value?.id || ''))
    const selectedNewPalletCode = computed(() => String(selectedNewPallet.value?.palletCode || ''))

    const newWareTextComputed = computed(() => String(newWareText.value || '').trim())

    const showWareSelector = ref(false)
    const wareSelectorLoading = ref(false)
    const wareSelectorError = ref('')
    const warehouseName = ref('')
    const wareAreas = ref([])
    const activeAreaId = ref('')
    const activeAreaName = ref('')
    const selectedArea = ref(null)
    const selectedLocation = ref(null)
    const selectedLocationId = computed(() => String(selectedLocation.value?.id || ''))

    const activeLocations = computed(() => {
      const area = wareAreas.value.find((a) => String(a.id) === String(activeAreaId.value))
      return Array.isArray(area?.wareList) ? area.wareList : []
    })

    let palletSearchTimer = null

    const materialList = computed(() => {
      const d = detail.value || {}
      const list = Array.isArray(d.materialList) ? d.materialList : []
      return list
    })

    const formatQty = (val) => {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      if (Number.isNaN(n)) return String(val)
      return n.toFixed(3)
    }

    const fetchDetail = async () => {
      const code = palletCode.value
      if (!code) {
        error.value = '缺少托盘码'
        return
      }

      loading.value = true
      error.value = ''
      try {
        const res = await getPalletDetail({ palletCode: code })
        detail.value = res?.data || null
      } catch (e) {
        detail.value = null
        error.value = e?.message || '加载失败'
        toast.error(error.value)
      } finally {
        loading.value = false
      }
    }

    const openMoveModal = () => {
      newWareId.value = ''
      newWareText.value = ''
      selectedArea.value = null
      selectedLocation.value = null
      palletDropdownOpen.value = false
      palletKeyword.value = ''
      palletError.value = ''
      palletOptions.value = []
      selectedNewPallet.value = null
      showMoveModal.value = true
    }

    const closeMoveModal = () => {
      showMoveModal.value = false
      palletDropdownOpen.value = false
    }

    const getSourceParentId = () => {
      const d = detail.value || {}
      const materials = Array.isArray(d.materialList) ? d.materialList : []
      for (const m of materials) {
        const stockRows = Array.isArray(m?.stockList) ? m.stockList : []
        for (const s of stockRows) {
          const storeId = asId(s?.storeId)
          const wareId = asId(s?.wareId)
          if (storeId) return storeId
          if (wareId) return wareId
        }
      }

      const storeId = asId(d?.storeId) || asId(d?.store?.id)
      return storeId || asId(d?.wareId) || ''
    }

    const openWareSelector = async () => {
      const parentId = getSourceParentId()
      if (!parentId) {
        toast.error('缺少仓库/库位信息')
        return
      }

      showWareSelector.value = true
      wareSelectorError.value = ''

      if (wareAreas.value.length) return

      wareSelectorLoading.value = true
      try {
        const res = await getWareListByParentId({ id: parentId })
        const data = res?.data
        warehouseName.value = data?.wareName || ''
        const nextAreas = Array.isArray(data?.wareList) ? data.wareList : []
        wareAreas.value = nextAreas
        const first = nextAreas[0]
        activeAreaId.value = first?.id || ''
        activeAreaName.value = first?.wareName || first?.wareNo || ''
      } catch (e) {
        wareSelectorError.value = e?.message || '加载失败'
      } finally {
        wareSelectorLoading.value = false
      }
    }

    const closeWareSelector = () => {
      showWareSelector.value = false
    }

    const selectArea = (area) => {
      activeAreaId.value = area?.id || ''
      activeAreaName.value = area?.wareName || area?.wareNo || ''
    }

    const selectLocation = (loc) => {
      const area = wareAreas.value.find((a) => String(a.id) === String(activeAreaId.value)) || null
      selectedArea.value = area
      selectedLocation.value = loc
    }

    const confirmWareSelector = () => {
      if (!selectedLocationId.value) return
      newWareId.value = String(selectedLocation.value?.id || '')
      const areaName = selectedArea.value?.wareName
      const locName = selectedLocation.value?.wareName
      newWareText.value = areaName && locName ? `${areaName} / ${locName}` : String(locName || areaName || '')
      closeWareSelector()
    }

    const asId = (val) => {
      const s = String(val ?? '').trim()
      if (!s) return ''
      if (!/^\d+$/.test(s)) return ''
      return s
    }

    const normalizeStockQty = (val) => {
      const n = Number(val)
      if (!Number.isFinite(n) || n <= 0) return null
      return n
    }

    const handlePalletSearch = async () => {
      const keyword = String(palletKeyword.value || '').trim()
      palletError.value = ''
      palletOptions.value = []
      if (!keyword) return

      palletLoading.value = true
      try {
        const res = await getPalletCodeList({ palletCode: keyword })
        const data = res?.data
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
        palletOptions.value = list
        maybeAutoSelectPallet()
      } catch (e) {
        palletError.value = e?.message || '搜索失败'
      } finally {
        palletLoading.value = false
      }
    }

    const selectNewPallet = (p) => {
      if (!p?.id) return
      selectedNewPallet.value = p
    }

    const confirmNewPallet = (p) => {
      selectNewPallet(p)
      palletKeyword.value = String(p?.palletCode || '')
      palletDropdownOpen.value = false
    }

    const togglePalletDropdown = () => {
      palletDropdownOpen.value = !palletDropdownOpen.value
      if (palletDropdownOpen.value) {
        palletKeyword.value = ''
        palletError.value = ''
        palletOptions.value = []
      }
    }

    const openPalletDropdown = () => {
      palletDropdownOpen.value = true
      palletError.value = ''
      palletOptions.value = []
      if (String(palletKeyword.value || '').trim()) {
        handlePalletSearch()
      }
    }

    const handlePalletInput = async () => {
      selectedNewPallet.value = null
      palletDropdownOpen.value = true
      if (palletSearchTimer) clearTimeout(palletSearchTimer)
      palletSearchTimer = setTimeout(() => {
        handlePalletSearch()
      }, 300)
    }

    const maybeAutoSelectPallet = () => {
      const keyword = String(palletKeyword.value || '').trim()
      if (!keyword) return
      const list = Array.isArray(palletOptions.value) ? palletOptions.value : []
      const exact = list.find((x) => String(x?.palletCode || '').trim() === keyword)
      if (!exact) return
      selectedNewPallet.value = exact
      palletDropdownOpen.value = false
    }

    const occupyTextBy = (val) => {
      if (val === 0 || val === '0') return '空闲'
      if (val === 1 || val === '1') return '占用'
      return '-'
    }

    const badgeClassByOccupy = (val) => {
      if (val === 0 || val === '0') return 'free'
      if (val === 1 || val === '1') return 'busy'
      return 'unknown'
    }

    const selectedBatchMap = ref({})

    const batchKeyOf = (m, s) => {
      const materialId = asId(s?.materialId) || asId(m?.materialId) || asId(m?.id) || '0'
      const wareId = asId(s?.wareId) || asId(m?.wareId) || asId(detail.value?.wareId) || '0'
      const palletId = asId(s?.palletId) || asId(m?.palletId) || asId(detail.value?.palletId) || '0'
      const batchNo = String(s?.batchNo || m?.batchNo || '').trim() || '-'
      return `${materialId}_${wareId}_${palletId}_${batchNo}`
    }

    const isBatchSelected = (m, s) => {
      const key = batchKeyOf(m, s)
      return Boolean(selectedBatchMap.value?.[key])
    }

    const toggleBatch = (m, s) => {
      const key = batchKeyOf(m, s)
      const next = { ...(selectedBatchMap.value || {}) }
      if (next[key]) {
        delete next[key]
      } else {
        next[key] = true
      }
      selectedBatchMap.value = next
    }

    const buildStockCheckDetailList = () => {
      const d = detail.value || {}
      const list = []
      const materials = Array.isArray(d.materialList) ? d.materialList : []

      for (const m of materials) {
        const stockRows = Array.isArray(m?.stockList) && m.stockList.length ? m.stockList : [m]
        for (const s of stockRows) {
          if (!isBatchSelected(m, s)) continue

          const batchNo = String(s?.batchNo || m?.batchNo || '').trim() || '-'
          const moveNum = normalizeStockQty(s?.stockNum ?? m?.stockNum)
          if (!moveNum) {
            return { ok: false, list: [], error: `批次 ${batchNo} 库存为0，无法移位` }
          }

          const storeId =
            asId(s?.storeId) ||
            asId(m?.storeId) ||
            asId(d?.storeId) ||
            asId(d?.store?.id) ||
            ''
          const materialId = asId(s?.materialId) || asId(m?.materialId) || asId(m?.id)
          const wareId = asId(s?.wareId) || asId(m?.wareId) || asId(d?.wareId)
          const palletId = asId(s?.palletId) || asId(m?.palletId) || asId(d?.palletId) || asId(d?.id)

          if (!storeId || !materialId || !wareId || !palletId || batchNo === '-') {
            return { ok: false, list: [], error: `批次 ${batchNo} 数据不完整，无法移位` }
          }

          list.push({
            storeId,
            materialId,
            wareId,
            palletId,
            batchNo,
            moveNum
          })
        }
      }

      return { ok: true, list, error: '' }
    }

    const submitMove = async () => {
      const wareId = asId(newWareId.value)
      const palletId = asId(selectedNewPallet.value?.id)
      if (!wareId) {
        toast.error('请选择新库位')
        return
      }
      if (!palletId) {
        toast.error('请选择新托盘码（需从下拉结果中选中）')
        return
      }

      const built = buildStockCheckDetailList()
      if (!built.ok) {
        toast.error(built.error || '移位明细生成失败')
        return
      }
      const stockCheckDetailList = built.list
      if (!stockCheckDetailList.length) {
        toast.error('请先勾选要移位的批次')
        return
      }

      const totalMoveNum = stockCheckDetailList.reduce((sum, it) => {
        const n = Number(it?.moveNum)
        return sum + (Number.isFinite(n) ? n : 0)
      }, 0)
      if (!totalMoveNum) {
        toast.error('请先勾选要移位的批次')
        return
      }

      submitLoading.value = true
      try {
        const d = detail.value || {}
        await changePalletLocation({
          fromPalletId: asId(d?.id),
          fromWareId: asId(d?.wareId),
          storeId: asId(d?.storeId) || asId(d?.store?.id),
          wareId,
          palletId,
          moveNum: totalMoveNum,
          stockCheckDetailList
        })
        toast.success('移位成功')
        closeMoveModal()
        await fetchDetail()
      } catch (e) {
        toast.error(e?.message || '移位失败')
      } finally {
        submitLoading.value = false
      }
    }

    const handleBack = () => {
      router.back()
    }

    onMounted(() => {
      fetchDetail()
    })

    onUnmounted(() => {
      if (palletSearchTimer) clearTimeout(palletSearchTimer)
    })

    return {
      loading,
      error,
      detail,
      occupyText,
      materialList,
      palletCode,
      formatQty,
      handleBack,
      showMoveModal,
      newWareId,
      newWareText: newWareTextComputed,
      palletDropdownOpen,
      palletKeyword,
      palletLoading,
      palletError,
      palletOptions,
      selectedNewPalletId,
      selectedNewPalletCode,
      openWareSelector,
      showWareSelector,
      closeWareSelector,
      wareSelectorLoading,
      wareSelectorError,
      warehouseName,
      wareAreas,
      activeAreaId,
      activeAreaName,
      activeLocations,
      selectedLocationId,
      selectArea,
      selectLocation,
      confirmWareSelector,
      submitLoading,
      openMoveModal,
      closeMoveModal,
      submitMove,
      isBatchSelected,
      toggleBatch,
      handlePalletSearch,
      selectNewPallet,
      confirmNewPallet,
      openPalletDropdown,
      handlePalletInput,
      maybeAutoSelectPallet,
      togglePalletDropdown,
      occupyTextBy,
      badgeClassByOccupy
    }
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--background-color);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-icon {
  width: 22px;
  height: 22px;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--header-text);
}

.header-right {
  width: 40px;
  height: 40px;
}

.content {
  padding: 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
  padding-bottom: calc(76px + env(safe-area-inset-bottom));
}

.batch-stock {
  width: 72px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  text-align: right;
}

.batch-check {
  display: flex;
  align-items: center;
  gap: 8px;
}

.batch-check-input {
  width: 16px;
  height: 16px;
}

.action-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background: rgba(30, 41, 59, 0.96);
  backdrop-filter: blur(6px);
  box-sizing: border-box;
  z-index: 1500;
}

.action-btn {
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 12px;
  background: var(--primary-color);
  color: var(--primary-text);
  font-size: 15px;
  font-weight: 700;
}

.action-btn:disabled {
  opacity: 0.6;
}

.status {
  padding: 16px 0;
  text-align: center;
  color: var(--text-secondary);
}

.panel {
  background: var(--card-background);
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 2px 10px var(--shadow-light);
  border: 1px solid var(--border-color);
  margin-bottom: 12px;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.row {
  display: grid;
  grid-template-columns: 78px 1fr;
  align-items: center;
  gap: 10px;
}

.label {
  font-size: 12px;
  color: var(--text-secondary);
}

.value {
  font-size: 13px;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.material-card {
  background: var(--card-background);
  border-radius: 12px;
  padding: 12px;
  border: 1px solid var(--border-color);
}

.material-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}

.material-no {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.material-qty {
  font-size: 14px;
  font-weight: 700;
  color: var(--primary-color);
}

.material-sub {
  margin-top: 6px;
  font-size: 13px;
  color: var(--text-primary);
}

.material-meta {
  margin-top: 8px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
}

.meta-item {
  font-size: 12px;
  color: var(--text-secondary);
}

.batch {
  margin-top: 10px;
  background: var(--card-background);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.batch-title {
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  background: rgba(35, 177, 192, 0.12);
  border-bottom: 1px solid var(--border-color);
}

.batch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  gap: 10px;
}

.batch-left {
  flex: 1 1 auto;
  min-width: 0;
}

.batch-no {
  font-size: 12px;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.batch-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

.batch-row + .batch-row {
  border-top: 1px solid var(--border-color);
}



@media (max-width: 480px) {
  .value {
    white-space: normal;
    word-break: break-all;
  }
}

.modal {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
}

.modal-panel {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 520px;
  background: var(--card-background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 36px var(--shadow-light);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px;
  border-bottom: 1px solid var(--border-color);
}

.modal-back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 36px;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.modal-right {
  width: 36px;
  height: 36px;
}

.modal-body {
  padding: 12px;
}

.form-row {
  display: grid;
  grid-template-columns: 78px 1fr;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}

.form-row-btn {
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  grid-template-columns: 78px 1fr 24px;
}

.form-value {
  font-size: 13px;
  color: var(--text-primary);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.form-value.placeholder {
  color: var(--text-secondary);
}

.form-arrow {
  font-size: 16px;
  color: var(--text-secondary);
  text-align: right;
}

.form-label {
  font-size: 12px;
  color: var(--text-secondary);
}

.form-input {
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  padding: 0 12px;
  font-size: 13px;
  box-sizing: border-box;
  width: 100%;
}

.form-tip {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.dropdown-wrap {
  position: relative;
  width: 100%;
}

.dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 38px;
  z-index: 10;
}

.pallet-options {
  margin-top: 6px;
  max-height: 260px;
  overflow: auto;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-background);
}

.pallet-option {
  width: 100%;
  text-align: left;
  border: none;
  background: var(--card-background);
  padding: 10px 12px;
}

.pallet-option-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.pallet-badge {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
}

.pallet-badge.free {
  color: #10b981;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.pallet-badge.busy {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.pallet-badge.unknown {
  color: var(--text-secondary);
  background: rgba(35, 177, 192, 0.12);
  border: 1px solid var(--border-color);
}

.pallet-option + .pallet-option {
  border-top: 1px solid var(--border-color);
}

.pallet-option.active {
  background: rgba(35, 177, 192, 0.12);
}

.pallet-option-main {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.pallet-option-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.modal-footer {
  padding: 12px;
  border-top: 1px solid var(--border-color);
}

.selector-confirm {
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 12px;
  background: var(--primary-color);
  color: var(--primary-text);
  font-size: 15px;
  font-weight: 700;
}

.selector-confirm:disabled {
  opacity: 0.6;
}

.selector {
  position: fixed;
  inset: 0;
  z-index: 2100;
}

.selector-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
}

.selector-panel {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 680px;
  background: var(--card-background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 36px var(--shadow-light);
  display: flex;
  flex-direction: column;
}

.selector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid var(--border-color);
}

.selector-back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 36px;
}

.selector-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.selector-right {
  width: 36px;
  height: 36px;
}

.selector-warehouse {
  padding: 10px 12px;
  font-size: 13px;
  color: var(--text-primary);
  background: rgba(35, 177, 192, 0.12);
  border-bottom: 1px solid var(--border-color);
}

.selector-loading,
.selector-error {
  padding: 16px 12px;
  text-align: center;
  color: var(--text-secondary);
}

.selector-body {
  display: grid;
  grid-template-columns: 140px 1fr;
  min-height: 320px;
  flex: 1 1 auto;
  overflow: hidden;
  overscroll-behavior: contain;
}

.areas {
  border-right: 1px solid var(--border-color);
  padding: 10px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.area-item {
  width: 100%;
  border: none;
  background: var(--card-background);
  padding: 10px 10px;
  border-radius: 10px;
  text-align: left;
  font-size: 13px;
  color: var(--text-primary);
  min-height: 40px;
}

.area-item + .area-item {
  margin-top: 8px;
}

.area-item.active {
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  font-weight: 700;
}

.locations {
  padding: 10px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.locations-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.location-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.location-card {
  border: 1px solid var(--border-color);
  background: var(--card-background);
  border-radius: 12px;
  padding: 10px;
  text-align: left;
  min-height: 54px;
}

.location-card.selected {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.12);
}

.location-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.location-sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.selector-footer {
  padding: 12px;
  border-top: 1px solid var(--border-color);
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
}

@media (max-width: 480px) {
  .selector-panel {
    left: 0;
    right: 0;
    top: auto;
    bottom: 0;
    transform: none;
    width: 100%;
    max-width: none;
    border-radius: 16px 16px 0 0;
    height: calc(100vh - env(safe-area-inset-top));
  }

  .selector-body {
    grid-template-columns: 104px 1fr;
    min-height: 0;
  }

  .location-grid {
    grid-template-columns: 1fr;
  }
}
</style>
